import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_stock_alerts,
  set_variant_stock,
  unwatch_variant,
  watch_variant,
  type Vertical,
} from './api';

const ALERTS_KEY = ['stock-alerts'] as const;

/**
 * Saving a count invalidates the caller's product list rather than patching it: a stock
 * change can flip `is_in_stock`, which both the vendor's list and the shop render, and
 * hand-patching two shapes is how one of them goes stale.
 */
export function use_set_variant_stock(invalidate: readonly unknown[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      vertical,
      variant_id,
      stock_qty,
    }: {
      vertical: Vertical;
      variant_id: string;
      stock_qty: number | null;
    }) => set_variant_stock(vertical, variant_id, stock_qty),
    onSuccess: () => qc.invalidateQueries({ queryKey: invalidate }),
  });
}

export function use_watch_variant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vertical, variant_id }: { vertical: Vertical; variant_id: string }) =>
      watch_variant(vertical, variant_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  });
}

export function use_unwatch_variant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variant_id: string) => unwatch_variant(variant_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  });
}

export function use_stock_alerts(enabled = true) {
  return useQuery({ queryKey: ALERTS_KEY, queryFn: fetch_stock_alerts, enabled });
}
